<template>
  <div class="pricing" :class="{'is-profile': profile}">
    <div v-if="loading">loading ...</div>
    <div data-test="pricing-container" class="pricing-container" :style="{ backgroundColor: color }">
      <!-- <div class="pricing-container__image" v-if="image" :style="`background-image: url(${image});`"></div> -->
      <h1 class="pricing-heading" data-test="signup-heading">Sign Up for {{channel.name}}</h1>
      <h2 class="pricing-subheading">{{channel.description}}</h2>
      <cv-radio-group data-test="pricing-tiers" :vertical="true" class="pricing-tiers">
        <div class="pricing-tiers__item" v-for="tier in tiers" :key="tier.name">
          <div class="pricing-tiers__item-group">
            <cv-radio-button  name="group-1" :label="`${tier.displayName}`" :value="tier.name" v-model="activeTier"/>
            <span class="pricing-tiers__item-price">{{(parseFloat(tier.price) || 0).toFixed(2)}} / mo.</span>
          </div>
          <div class="pricing-tiers__item-description">{{tier.description}}</div>
        </div>
        <div class="pricing-tiers__item" data-test="radio-free">
          <cv-radio-button key="free" name="group-1" label="Free Posts Only" value="free" v-model="activeTier"/>
          <div class="pricing-tiers__item-description">Occasional Public Posts</div>
        </div>
      </cv-radio-group>
      <div v-if="activeTier && activeTier != 'free'" class="pricing-form">
        <stripe-payment :tier="activeTier"/>
      </div>
      <div v-else class="pricing-submit">
        <ff-button
          v-if="!buttonLoading && showCTA"
          class="pricing-submit__button"
          data-test="subscribe-free-cta"
          :options="{ width: 100 }"
          :text="currentTier !== 'free' ? 'Unsubscribe' : 'Continue Without Subscribing'"
          :action="register"/>
        <ff-loading data-test="free-loading" v-if="buttonLoading"/>
        <aside class="pricing-noneed" v-if="!showCTA && profile">
          This is your current membership.
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/functions'
import { mapState } from 'vuex'
import persist from '../../mixins/persist'
import analytics from '../../mixins/analytics'
import email from '../../mixins/email'
import FFButton from '../DesignSystem/FFButton'
import FFLoading from '../DesignSystem/FFLoading'
import StripePayment from '../_Shared/StripePayment'

export default {
  name: 'subscribe',
  props: ['profile'],
  mixins: [
    persist,
    analytics,
    email
  ],
  components: {
    'stripe-payment': StripePayment,
    'ff-button': FFButton,
    'ff-loading': FFLoading
  },
  data () {
    return {
      activeTier: 'paid',
      buttonLoading: false
    }
  },
  computed: {
    ...mapState(['user', 'channel', 'channel_id']),
    loading () {
      return !this.channel
    },
    color () {
      if (!this.channel || !this.channel.color) return
      return `#${this.channel.color}`
    },
    tiers () {
      let ourTiers = []
      if (this.channel.tiers) {
        for (let key in this.channel.tiers) {
          let val = this.channel.tiers[key]
          val.name = key
          ourTiers.push(val)
        }
      }
      return ourTiers
    },
    noTiers () {
      return !this.channel.tiers
    },
    isOwner () {
      return this.$store.state.channel.isOwner
    },
    stripeConnected () {
      return !!this.$store.state.channel.stripeAccountId
    },
    currentTier () {
      if (this.channel.fans && this.channel.fans[this.user.uid]) {
        return this.channel.fans[this.user.uid].tier
      }

      return 'free'
    },
    showCTA () {
      if (!this.profile) return true
      if (!this.channel.fans || !this.channel.fans[this.user.uid]) return false
      return this.channel.fans[this.user.uid].tier != this.activeTier
    }
  },
  mounted () {
    if (!this.user) {
      return this.$router.push({ name: 'channel-landing' })
    }
    if (this.profile) {
      if (this.channel.fans && this.channel.fans[this.user.uid]) {
        this.activeTier = this.channel.fans[this.user.uid].tier
        // and dont show button if it's not different
      }
    }
  },
  methods: {
    async register () {
      this.buttonLoading = true
      let func = firebase.functions().httpsCallable('stripe-createSubscription')
      await func({channelId: this.channel_id, tier: 'free'})
      this.$router.push({name: 'channel-landing'})
      this.track('[Channel] Subscribed', { tier: 'free' })

      // Email the owner
      const user = this.user.email ? `Email: <strong>${this.user.email}<strong/>` : `Someone`
      return this.emailToOwner({
        html: `${user} subscribed to your channel.<br/><br/>Keep up the good work!`,
        subject: `New free subscriber to your channel ${this.channel.name}!`
      })
    }
  }
}
</script>

<style scoped lang="scss">
  @import "../../assets/styles/app.scss";

  .pricing {
    // margin: $unit-3 0;
  }

  .pricing-container {
    max-width: 32rem;
    margin: $unit-3*3 auto;
    background: $color-almost-white;
    padding: $unit-3;
    border-radius: $borderRadius;
  }

  .pricing-heading {
    font-weight: 600;
    font-size: $fontSize-medium;
    margin-bottom: $unit-1/2;
  }

  .pricing-subheading {
    font-size: $fontSize-medium/1.2;
    font-weight: 400;
    opacity: 0.6;
  }

  .pricing-aside {
    @include aside;
  }

  .pricing-tiers {
    width: 100%;
    margin: 0 0 $unit-3;

    ::v-deep .bx--radio-button-group {
      width: 100%;
    }
  }

  .pricing-tiers__item {
    margin: auto;
    padding: $unit-2 $unit-1 0;
    width: 100%;
    text-align: left;
    color: $color-dark;

    &-group {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &-price {
      font-weight: 600;
      font-size: $fontSize-normal;
    }

    ::v-deep .bx--radio-button__label {
      font-size: $fontSize-normal;
      font-weight: 600;
      justify-content: flex-start;
    }
  }

  .pricing-tiers__item-description {
    font-size: $fontSize-normal;
    color: $color-dark;
    margin: $unit-1/2 0 0 1.625rem;
  }

  .pricing-submit {
    margin: $unit-2 auto 0;
    width: 100%;
  }

  .pricing-submit__button {
    margin: 0;
  }

  .pricing-noneed {
    @include success;
  }

  .is-profile {
    .pricing-container {
      margin: 0;
    }
  }

</style>
